import { MdsIconKind } from "@/design-system/components/icon";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIconButton,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { PendingApiKey } from "@/pages/integrations/api/integrations-api-page.types";
import { observer } from "mobx-react-lite";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { useCallback } from "react";
import {
  ExpandableContent,
  StyledForm,
  StyledSection,
} from "@/pages/integrations/shared/integrations-form";
import { StyledSubTitle } from "@/pages/integrations/shared/integrations.styles";

type TProps = {
  nickname: string;
  pendingApiKey: PendingApiKey | null;
  copyApiKey: () => void;
  handleCloseForm: () => void;
  setNickname: (nickname: string) => void;
};

export const IntegrationsApiPageContentForm = observer<TProps>(
  ({ nickname, pendingApiKey, copyApiKey, handleCloseForm, setNickname }: TProps) => {
    const { store } = useAppStore();

    const submitForm = useCallback(() => {
      handleCloseForm();
      setNickname("");

      if (!pendingApiKey) {
        return;
      }

      store.apiKeys.updateApiKey({
        id: pendingApiKey.id,
        title: nickname || null,
      });

      trackEvent(TrackedEvent.ApiKeyName, {
        api_key_id: pendingApiKey.id,
        api_key_name: nickname,
      });
    }, [handleCloseForm, nickname, pendingApiKey, setNickname, store.apiKeys]);

    return (
      <StyledForm>
        <StyledSection>
          <StyledSubTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
            Add new API key
          </StyledSubTitle>
          <Disclaimer>
            Make sure to save your secret key;{" "}
            <EmphasizedText>this key won&apos;t be shown again.</EmphasizedText>
          </Disclaimer>
          <SecretKeyText>
            {`sk-mem-${pendingApiKey?.secretKeyToken}`}
            <MDSIconButton iconKind={MdsIconKind.Copy} onClick={copyApiKey} />
          </SecretKeyText>
          <ExpandableContent>
            <MDSTextField
              variant={MDSTextFieldVariant.Form}
              value={nickname}
              onChange={setNickname}
              placeholder="Enter nickname"
              aria-label="Nickname"
              autoFocus
            />
          </ExpandableContent>
          <FooterStyles>
            <MDSButton variant={MDSButtonVariant.Contrast} onClick={submitForm}>
              Done
            </MDSButton>
          </FooterStyles>
        </StyledSection>
      </StyledForm>
    );
  }
);

const Disclaimer = styled.div(({ theme }) => ({
  color: theme.colors.text.primary,
  fontSize: theme.fontSizes.small,
}));

const FooterStyles = styled.div({
  alignSelf: "flex-end",
  display: "flex",
  gap: "8px",
});

const SecretKeyText = styled.div(({ theme }) => ({
  fontFamily: "monaco, monospace",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing.sm,
  padding: `1px ${theme.spacing.sm}`,
  backgroundColor: theme.colors.grey.x25,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.borderRadius.medium,
  color: theme.colors.text.primary,
  width: "100%",

  "& svg": {
    height: 16,
    width: 16,
    color: theme.colors.grey.x400,
  },
}));

const EmphasizedText = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
}));
